/**
 * DetailChecklist — Binds useChecklistItems to ChecklistContainer.
 *
 * Uses non-suspending `useLiveQueryImmediate` so the list never
 * flashes when items are added/removed. Real items are passed to
 * ChecklistContainer which manages its own pending-ops optimistic state.
 *
 * The "add checklist" toggle icon lives in DetailActions.
 */

import { memo } from "react"
import type { DbObject } from "@/db/schema"
import { useChecklistItems } from "@/features/task-detail/hooks/useChecklistItems"
import { ChecklistContainer } from "./checklist/ChecklistContainer"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DetailChecklistProps {
  task: DbObject
}

// ---------------------------------------------------------------------------
// Component (non-suspending — no Suspense wrapper needed)
// ---------------------------------------------------------------------------

export const DetailChecklist = memo(function DetailChecklist({
  task,
}: DetailChecklistProps) {
  const taskId = task.id
  if (!taskId) return null

  return <DetailChecklistContent task={task} taskId={taskId} />
})

/** Extracted so hooks are only called when taskId is defined. */
function DetailChecklistContent({
  task,
  taskId,
}: {
  task: DbObject
  taskId: string
}) {
  const [realItems] = useChecklistItems(taskId, task.parentPath)

  // No items — render nothing (ChecklistContainer also returns null for 0 items,
  // but we short-circuit here to avoid mounting the container at all)
  if (realItems.length === 0) {
    return null
  }

  return (
    <ChecklistContainer
      realItems={realItems}
      taskId={taskId}
      taskParentPath={task.parentPath}
      taskRealmId={task.realmId ?? ""}
      readOnlyToggle={task.isRecurringTemplate === 1}
    />
  )
}
